import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trophy } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState, PageHeader, ScoreRing, Spinner } from "@/components/shared";
import { cn } from "@/lib/utils";

const RANK_LABELS: Record<string, string> = {
  compliance: "Rank by: compliance",
  revenue: "Rank by: revenue",
  profit: "Rank by: profit",
  roi: "Rank by: ROI per km",
  safety: "Rank by: safety",
  volume: "Rank by: trips",
  balanced: "Rank by: balanced",
};

const money = (v: number | null | undefined, cur: string) =>
  v == null ? "—" : new Intl.NumberFormat(undefined, { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v);

export default function Scorecard() {
  const [days, setDays] = useState(7);
  const [rankBy, setRankBy] = useState<string>(() => localStorage.getItem("tg.scorecard.rank") || "compliance");
  const pickRank = (m: string) => { setRankBy(m); try { localStorage.setItem("tg.scorecard.rank", m); } catch { /* ignore */ } };
  const { data, isLoading } = useQuery({ queryKey: ["scorecard", days, rankBy], queryFn: async () => (await api.get("/drivers/scorecard", { params: { days, rankBy } })).data, refetchInterval: 60000 });
  const cur = data?.currency || "EUR";
  return (
    <div data-testid="scorecard-page">
      <PageHeader title="Driver scorecard" subtitle={`${data?.rankModes?.[rankBy] || "Ranked drivers"} · since ${data ? fmtDateTime(data.since) : "…"} · "requires review" is not a verdict`} actions={
        <div className="flex gap-2">
          <select data-testid="scorecard-rank-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100"
                  value={rankBy} onChange={(e) => pickRank(e.target.value)} title={data?.rankModes?.[rankBy]}>
            {Object.keys(data?.rankModes || { compliance: "" }).map((k) => (
              <option key={k} value={k}>{RANK_LABELS[k] || k}</option>
            ))}
          </select>
          <select data-testid="scorecard-days-select" className="h-9 rounded-md bg-[#0f141f] border border-[#26334d] text-sm px-3 text-slate-100" value={days} onChange={(e) => setDays(+e.target.value)}>
            {[[7, "Last 7 days"], [14, "Last 14 days"], [30, "Last 30 days"]].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>} />
      {data && !data.moneyAvailable && (
        <div className="tg-card px-4 py-2.5 mb-3 font-mono text-[11px] text-amber-300/90">
          No platform trips imported for this window, so revenue and profitability are blank. Import an Uber or Bolt export to fill them.
        </div>
      )}
      {data?.coverage?.missing?.length ? (
        <div className="tg-card px-4 py-2.5 mb-3 font-mono text-[11px] text-slate-400">
          Fuel cost not calculated for {data.coverage.missing.join(", ")} — set the fuel type on those vehicles.
        </div>
      ) : null}
      {isLoading ? <Spinner /> : !data?.drivers?.length ? <EmptyState text="No driver activity in this window" /> : (
        <div className="tg-card overflow-hidden">
          <Table data-testid="scorecard-table">
            <TableHeader><TableRow className="border-[#26334d] hover:bg-transparent">{["#", "Driver", "Score", "Trips", "Revenue", "Fuel", "Net", "Per km", "Profit #", "Journeys", "km", "Matched", "Requires review", "Harsh alerts"].map((h) => <TableHead key={h} className="tg-label">{h}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.drivers.map((d: any) => (
                <TableRow key={d.driverId} data-testid={`scorecard-row-${d.driverId}`} className="border-[#26334d] hover:bg-[#1f2a3e]">
                  <TableCell className="font-mono text-slate-400">{d.rank <= 3 ? <Trophy className={cn("h-4 w-4", d.rank === 1 ? "text-amber-400" : d.rank === 2 ? "text-slate-300" : "text-amber-700")} /> : d.rank}</TableCell>
                  <TableCell className="text-sm text-slate-100">{d.driver}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <ScoreRing score={Math.round(d.dimensions?.[data.rankBy] ?? d.score ?? 0)} size={36} testId={`scorecard-score-${d.driverId}`} />
                      <span className="font-mono text-[10px] text-slate-500 leading-tight">
                        {RANK_LABELS[data.rankBy]?.replace("Rank by: ", "") || "score"}
                        <br />{d.matchRate != null ? `${d.matchRate}% match` : "no trips"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-300">{d.trips || "—"}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-100">{money(d.revenue, cur)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{d.fuelCost == null ? <span title="No fuel data on this driver's vehicles">n/a</span> : money(d.fuelCost, cur)}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-400">{d.net == null ? "—" : money(d.net, cur)}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-400">{d.revenuePerKm == null ? "—" : `${d.revenuePerKm}`}</TableCell>
                  <TableCell className="font-mono text-xs">
                    {d.profitRank ? <span className={cn("px-1.5 py-0.5 rounded text-[10px]", d.profitRank <= 3 ? "bg-emerald-500/15 text-emerald-300" : "text-slate-500")}>#{d.profitRank}</span> : "—"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{d.journeys}</TableCell>
                  <TableCell className="font-mono text-xs">{d.km}</TableCell>
                  <TableCell className="font-mono text-xs text-emerald-400">{d.matched}</TableCell>
                  <TableCell className="font-mono text-xs text-amber-400">{d.review}</TableCell>
                  <TableCell className="font-mono text-xs"><span className={cn(d.harsh ? "text-rose-400" : "text-slate-500")}>{d.harsh}</span>{d.critical ? <span className="ml-1 text-[10px] text-rose-300 border border-rose-500/30 rounded px-1">{d.critical} critical</span> : null}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
